import { isAddress } from 'viem';
import { z } from 'zod';

/**
 * Common schema
 */
export const abiInputSchema = z.object({
  indexed: z.boolean(),
  internalType: z.string(),
  name: z.string(),
  type: z.string(),
});

export const abiItemSchema = z.object({
  anonymous: z.boolean(),
  inputs: z.array(abiInputSchema),
  name: z.string(),
  type: z.string(),
});

export const contractSchema = z.object({
  address: z
    .string()
    .min(1, { message: 'Address is required' })
    .refine((val) => isAddress(val), {
      message: 'Address is invalid',
    }),
  chain: z.number().min(1, { message: 'Chain is required' }),
  name: z.string().min(1, { message: 'Name is required' }),
  start_block: z.number().optional(),
  abi: z.string().min(1, { message: 'ABI is required' }),
  events: z.array(abiItemSchema),
});

/**
 * Common type
 */
type Contract = z.infer<typeof contractSchema>;

/**
 * Create contract
 * Route: /:teamId/projects/:projectId/contracts
 * Method: POST
 */
export const createContractRequest = z.object({
  params: z.object({
    teamId: z.string().uuid(),
    projectId: z.string(),
  }),
  body: contractSchema,
});
export type CreateContractRequest = z.infer<typeof createContractRequest>;
export type CreateContractResponse = Contract;

/**
 * Get contract
 * Route: /:teamId/projects/:projectId/contracts
 * Method: GET
 */
export const getContractsOfProjectRequest = z.object({
  params: z.object({
    teamId: z.string().uuid(),
    projectId: z.string(),
  }),
});
export type GetContractsOfProjectRequest = z.infer<
  typeof getContractsOfProjectRequest
>;
export type GetContractsOfProjectResponse = Contract[];

/**
 * Delete contract
 * Route: /:teamId/projects/:projectId/contracts/:chain/:address
 * Method: DELETE
 */
export const deleteContractRequest = z.object({
  params: z.object({
    teamId: z.string().uuid(),
    projectId: z.string(),
    chain: z.string().refine((val) => !isNaN(Number(val)), {
      message: 'Chain must be a number',
    }),
    address: z.string(),
  }),
});
export type DeleteContractRequest = z.infer<typeof deleteContractRequest>;
export type DeleteContractResponse = null;

/**
 * Create contract
 * Route: /:teamId/projects/:projectId/contracts/:chain/:address
 * Method: PUT
 */
export const updateContractRequest = z.object({
  params: z.object({
    teamId: z.string().uuid(),
    projectId: z.string(),
    chain: z.string().refine((val) => !isNaN(Number(val)), {
      message: 'Chain must be a number',
    }),
    address: z.string(),
  }),
  body: contractSchema,
});
export type UpdateContractRequest = z.infer<typeof updateContractRequest>;
export type UpdateContractResponse = Contract;
